import { useState } from "react";
import { motion } from "framer-motion";
import { useAffiliate, AFFILIATE_PLAN_COMMISSIONS } from "@/contexts/AffiliateContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Users, Wallet, TrendingUp, IndianRupee, Copy, Share2,
  CheckCircle2, Clock, ExternalLink, ShoppingCart, MousePointerClick,
  UserPlus, Banknote, CreditCard, Smartphone, ArrowUpCircle, Crown,
} from "lucide-react";

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const COLORS = {
  accent: "hsl(var(--accent))",
  success: "hsl(142 71% 45%)",
  warning: "hsl(38 92% 50%)",
  destructive: "hsl(0 84% 60%)",
  muted: "hsl(var(--muted-foreground))",
};

const tooltipStyle = {
  contentStyle: {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    fontSize: "12px",
    color: "hsl(var(--foreground))",
  },
};

const refStatusCfg = {
  clicked: { label: "Clicked", color: "bg-muted text-muted-foreground", icon: ExternalLink },
  signed_up: { label: "Signed Up", color: "bg-accent/10 text-accent border-accent/20", icon: UserPlus },
  purchased: { label: "Purchased", color: "bg-success/10 text-success border-success/20", icon: ShoppingCart },
};

const commStatusCfg = {
  pending: { label: "Pending", color: "bg-muted text-muted-foreground" },
  approved: { label: "Approved", color: "bg-accent/10 text-accent border-accent/20" },
  paid: { label: "Paid", color: "bg-success/10 text-success border-success/20" },
};

const payoutStatusCfg = {
  requested: { label: "Requested", color: "bg-accent/10 text-accent border-accent/20", icon: Clock },
  processing: { label: "Processing", color: "bg-warning/10 text-warning border-warning/20", icon: ArrowUpCircle },
  completed: { label: "Completed", color: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
};

export default function AffiliateDashboardPage() {
  const { profile, referrals, commissions, payouts, stats, requestPayout } = useAffiliate();
  const [copied, setCopied] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState<"bank_transfer" | "upi" | "platform_credits">("bank_transfer");

  const copyLink = () => {
    navigator.clipboard.writeText(`https://${profile.link}`);
    setCopied(true);
    toast.success("Affiliate link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({ title: "Join AccouPro", text: "Try AccouPro — the smartest accounting platform for Indian businesses!", url: `https://${profile.link}` });
    } else copyLink();
  };

  const handlePayout = () => {
    const amt = Number(payoutAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt > stats.pendingBalance) { toast.error("Insufficient balance"); return; }
    const ok = requestPayout(amt, payoutMethod);
    if (ok) { toast.success(`Payout of ₹${amt} requested`); setPayoutAmount(""); }
  };

  // Chart data
  const monthlyData = (() => {
    const months: Record<string, { paid: number; approved: number; pending: number }> = {};
    commissions.forEach(c => {
      if (!months[c.month]) months[c.month] = { paid: 0, approved: 0, pending: 0 };
      months[c.month][c.status === "paid" ? "paid" : c.status === "approved" ? "approved" : "pending"] += c.amount;
    });
    return Object.entries(months).sort(([a], [b]) => a.localeCompare(b)).map(([m, v]) => ({
      month: new Date(m + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
      ...v,
    }));
  })();

  const funnelData = [
    { stage: "Clicks", value: stats.totalClicks, color: COLORS.muted },
    { stage: "Signups", value: stats.totalSignups, color: COLORS.accent },
    { stage: "Purchases", value: stats.totalPurchases, color: COLORS.success },
  ];

  const planBreakdown = (() => {
    const counts: Record<string, number> = {};
    referrals.filter(r => r.status === "purchased" && r.plan).forEach(r => {
      counts[r.plan!] = (counts[r.plan!] || 0) + 1;
    });
    return Object.entries(counts).map(([plan, count]) => ({
      name: AFFILIATE_PLAN_COMMISSIONS[plan]?.label || plan,
      value: count,
      color: plan === "enterprise" ? COLORS.success : plan === "professional" ? COLORS.accent : COLORS.muted,
    }));
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Affiliate Dashboard</h1>
        <p className="text-sm text-muted-foreground">Promote AccouPro and earn commission on every plan purchase</p>
      </div>

      {/* Affiliate Link */}
      <Card className="border-accent/30 bg-gradient-to-br from-accent/5 to-transparent">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
            <Share2 className="h-5 w-5 text-accent" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Your Affiliate Link</h2>
            <p className="text-xs text-muted-foreground">Share everywhere — social media, blogs, email, YouTube</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 px-4 py-3 rounded-lg bg-muted/40 border border-border font-mono text-sm text-foreground select-all">
            https://{profile.link}
          </div>
          <Button onClick={copyLink} variant="outline" size="icon" className="h-11 w-11">
            {copied ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
          </Button>
          <Button onClick={shareLink} size="icon" className="h-11 w-11">
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Commission Tiers */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3">Commission Per Plan Purchase</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(AFFILIATE_PLAN_COMMISSIONS).map(([key, val]) => {
            const icons: Record<string, typeof CreditCard> = { starter: CreditCard, professional: Crown, enterprise: TrendingUp };
            const Icon = icons[key] || CreditCard;
            return (
              <div key={key} className="p-3 rounded-lg bg-muted/20 border border-border">
                <Icon className="h-5 w-5 text-accent mb-2" />
                <p className="text-sm font-semibold text-foreground">{val.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Commission: <span className="font-bold text-success">₹{val.amount}</span>
                </p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Total Clicks", value: stats.totalClicks, icon: MousePointerClick, color: "text-muted-foreground" },
          { label: "Purchases", value: stats.totalPurchases, icon: ShoppingCart, color: "text-success" },
          { label: "Total Earned", value: `₹${stats.totalEarned}`, icon: IndianRupee, color: "text-accent" },
          { label: "Pending Balance", value: `₹${stats.pendingBalance}`, icon: Wallet, color: "text-warning" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div className="text-2xl font-bold font-display text-foreground">{s.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Conversion Funnel + Earnings Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Conversion Funnel</h3>
          <div className="space-y-3">
            {funnelData.map((stage, i) => (
              <div key={i}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{stage.stage}</span>
                  <span className="font-semibold text-foreground">{stage.value}</span>
                </div>
                <Progress value={stats.totalClicks > 0 ? (stage.value / stats.totalClicks) * 100 : 0} className="h-3" />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Conversion rate: <span className="font-bold text-accent">{stats.conversionRate}%</span> (click → purchase)
          </p>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Monthly Earnings</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip {...tooltipStyle} formatter={(v: number) => `₹${v}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="paid" name="Paid" fill={COLORS.success} stackId="a" radius={[0, 0, 0, 0]} />
              <Bar dataKey="approved" name="Approved" fill={COLORS.accent} stackId="a" />
              <Bar dataKey="pending" name="Pending" fill={COLORS.warning} stackId="a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Plan Breakdown Chart */}
      {planBreakdown.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Purchases by Plan</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={planBreakdown} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={4} strokeWidth={0}>
                {planBreakdown.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Referrals + Commissions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-accent" /> Referrals ({referrals.length})
          </h3>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {referrals.map((ref) => {
              const cfg = refStatusCfg[ref.status];
              const Icon = cfg.icon;
              return (
                <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-sm font-bold text-accent">
                      {ref.refereeName.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{ref.refereeName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {ref.refereeEmail}
                        {ref.plan && <span className="ml-1 text-accent capitalize">· {ref.plan}</span>}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${cfg.color} flex items-center gap-1`}>
                    <Icon className="h-2.5 w-2.5" /> {cfg.label}
                  </Badge>
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-accent" /> Commissions ({commissions.length})
          </h3>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {commissions.map((cm) => {
              const cfg = commStatusCfg[cm.status];
              return (
                <div key={cm.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{cm.refereeName}</p>
                    <p className="text-xs text-muted-foreground capitalize">{cm.plan} · {cm.month}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-bold text-foreground">₹{cm.amount}</span>
                    <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Payout Section */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Wallet className="h-4 w-4 text-accent" /> Payouts — Balance: ₹{stats.pendingBalance}
        </h3>

        <div className="p-3 rounded-lg bg-muted/20 border border-border space-y-3 mb-4">
          <div className="flex gap-2">
            {(["bank_transfer", "upi", "platform_credits"] as const).map((m) => {
              const labels = { bank_transfer: "Bank Transfer", upi: "UPI", platform_credits: "Credits" };
              const icons = { bank_transfer: Banknote, upi: Smartphone, platform_credits: CreditCard };
              const Icon = icons[m];
              return (
                <button key={m} onClick={() => setPayoutMethod(m)}
                  className={`flex-1 p-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                    payoutMethod === m ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent/40"
                  }`}>
                  <Icon className="h-3.5 w-3.5" /> {labels[m]}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input type="number" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} placeholder="Amount" min={1} className="flex-1" />
            <Button onClick={handlePayout} disabled={!payoutAmount}>Request Payout</Button>
          </div>
        </div>

        <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
          {payouts.map((po) => {
            const cfg = payoutStatusCfg[po.status];
            const Icon = cfg.icon;
            return (
              <div key={po.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">₹{po.amount} — {po.method === "bank_transfer" ? "Bank" : po.method === "upi" ? "UPI" : "Credits"}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(po.requestedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    {po.ref && ` · ${po.ref}`}
                  </p>
                </div>
                <Badge variant="outline" className={`text-[10px] ${cfg.color} flex items-center gap-1`}>
                  <Icon className="h-2.5 w-2.5" /> {cfg.label}
                </Badge>
              </div>
            );
          })}
          {payouts.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No payouts yet</p>}
        </div>
      </Card>
    </div>
  );
}
